import React from 'react';
import { createRoot } from 'react-dom/client';

import '@core/ga/ga';
import { setPage } from '@authFlow/utils/setPage';
import { initStorageManager } from '@authFlow/utils/signupHelpers';
import initSentry from '@utils/initSentry';
import { storageManager } from '@authFlow/utils/SessionStorageManager';
import { setWhiteTeamGA4Defaults, ga4ClickHandler } from '@utils/analytics';
import { getPersonalisedPlan } from '@authFlow/utils/getPersonalisedPlan';
import { updateUserStorageData } from '@authFlow/utils/updateUserStorageData';
import runReverseSemrushOne from '@authFlow/utils/experiments/runReverseSemrushOne';

import App from './App/App';

function loadLocaleData(locale: string) {
  return import(`../lang/compiled/${locale}.json`)
    .then((messages) => messages)
    .catch((err) => console.error('Error loading locale', err));
}

const SENTRY_DSN_AUTH_FLOW = process.env.SENTRY_DSN_AUTH_FLOW;
initSentry(SENTRY_DSN_AUTH_FLOW, 170);

initStorageManager();
setPage(window.location.pathname);
setWhiteTeamGA4Defaults('site');
ga4ClickHandler();

document.addEventListener('DOMContentLoaded', async () => {
  const app = document.querySelector('#app');

  if (app) {
    const root = createRoot(app);
    const {
      ssoEnv = 'rc',
      countryCode = 'xx',
      isChina = false,
      headerTitleLogin = '',
      signupOfferTitle = '',
      theme = 'default',
    } = window.__AUTH_STATE__;

    const csrfInput = document.querySelector(
      'input[name="csrfmiddlewaretoken"]',
    );

    if (csrfInput instanceof HTMLInputElement) {
      window.__AUTH_STATE__.csrf = csrfInput.value;
    } else {
      window.__AUTH_STATE__.csrf = null;
    }

    const locale = window?.sm2?.language || 'en';
    const messages = await loadLocaleData(locale);

    if (window?.copilotData) {
      storageManager.set({
        custom: 'copilot',
        copilotData: window.copilotData.data,
      });
    }

    runReverseSemrushOne();

    if (window?.sm2?.user?.email) {
      const storedPersonalisedPlan =
        storageManager.get('userData')?.personalisedPlan;
      const personalisedPlan = await getPersonalisedPlan();

      updateUserStorageData({
        personalisedPlan:
          window.location.pathname === '/signup/get-free-trial/'
            ? personalisedPlan
            : storedPersonalisedPlan,
      });
    }

    root.render(
      <App
        ssoEnv={ssoEnv}
        countryCode={countryCode}
        isChina={isChina}
        textCaptchaUrl="/sso/captcha"
        headerTitleLogin={headerTitleLogin}
        signupOfferTitle={signupOfferTitle}
        locale={locale}
        messages={messages}
        flowTheme={theme}
      />,
    );
  }
});
